import { ButtonHTMLAttributes, Ref } from 'react';
import { twMerge } from 'tailwind-merge';
import { LoadingDots } from '../LoadingDots';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  submitting?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-[12px] gap-1.5 rounded-md',
  md: 'h-9 px-3.5 text-[13px] gap-2 rounded-lg',
  lg: 'h-10 px-4 text-[14px] gap-2 rounded-lg',
};

const BASE =
  'group/btn inline-flex shrink-0 items-center justify-center font-medium tracking-tight outline-none transition-[background-color,box-shadow,color,transform] active:translate-y-px disabled:cursor-not-allowed disabled:active:translate-y-0 select-none';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: [
    'text-white',
    'bg-linear-[180deg,#2DD4BF_0%,#0D9488_100%]',
    'shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_1px_2px_rgba(0,0,0,0.35),0_0_0_1px_rgba(13,148,136,0.6)]',
    'hover:bg-linear-[180deg,#5EEAD4_0%,#14B8A6_100%]',
    'hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_2px_8px_rgba(20,184,166,0.25),0_0_0_1px_rgba(20,184,166,0.7)]',
    'active:bg-linear-[180deg,#0D9488_0%,#0F766E_100%]',
    'focus-visible:shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_0_0_2px_rgba(20,184,166,0.55),0_0_0_4px_rgba(20,184,166,0.18)]',
    'disabled:bg-linear-[180deg,#2DD4BF_0%,#0D9488_100%] disabled:opacity-40 disabled:shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_0_0_1px_rgba(13,148,136,0.3)]',
  ].join(' '),
  secondary: [
    'text-neutral-100',
    'bg-white/[0.04]',
    'shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08),inset_0_1px_0_rgba(255,255,255,0.04)]',
    'hover:bg-white/[0.07] hover:text-white',
    'hover:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14),inset_0_1px_0_rgba(255,255,255,0.06)]',
    'active:bg-white/[0.05]',
    'focus-visible:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18),0_0_0_2px_rgba(255,255,255,0.12)]',
    'disabled:text-neutral-500 disabled:bg-white/[0.02] disabled:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)]',
  ].join(' '),
  ghost: [
    'text-neutral-400',
    'hover:bg-white/[0.04] hover:text-white',
    'active:bg-white/[0.06]',
    'focus-visible:bg-white/[0.04] focus-visible:text-white focus-visible:shadow-[0_0_0_2px_rgba(255,255,255,0.1)]',
    'disabled:text-neutral-600 disabled:hover:bg-transparent disabled:hover:text-neutral-600',
  ].join(' '),
  danger: [
    'text-white',
    'bg-linear-[180deg,#F87171_0%,#DC2626_100%]',
    'shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_1px_2px_rgba(0,0,0,0.35),0_0_0_1px_rgba(220,38,38,0.55)]',
    'hover:bg-linear-[180deg,#FCA5A5_0%,#EF4444_100%]',
    'active:bg-linear-[180deg,#DC2626_0%,#B91C1C_100%]',
    'focus-visible:shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_0_0_2px_rgba(248,113,113,0.55),0_0_0_4px_rgba(248,113,113,0.18)]',
    'disabled:opacity-40',
  ].join(' '),
};

export const Button = ({
  className,
  children,
  variant = 'secondary',
  size = 'md',
  submitting,
  disabled,
  type = 'button',
  ref,
  ...rest
}: Props) => {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || submitting}
      data-loading={submitting ? 'true' : undefined}
      className={twMerge(
        BASE,
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        className,
      )}
      {...rest}
    >
      {submitting ? <LoadingDots className="text-current" /> : children}
    </button>
  );
};
